/**
 * PerformanceCard - Afișează scorul și performanța medicului
 */

import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Rating,
  Chip,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material'
import {
  Star as StarIcon,
} from '@mui/icons-material'

interface PerformanceCardProps {
  score: number
  stars: number
  rank?: number
  totalMedics?: number
  scorePercentage?: number
  breakdown?: {
    base_score: number
    completion_bonus: number
    urgency_bonus: number
    pickup_penalty: number
    finalize_penalty: number
    unassign_penalty: number
    overdue_penalty: number
  }
  loading?: boolean
}

export const PerformanceCard: React.FC<PerformanceCardProps> = ({
  score,
  stars,
  rank,
  totalMedics,
  scorePercentage,
  breakdown,
  loading = false,
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 4.0) return 'success.main'
    if (score >= 3.0) return 'warning.main'
    return 'error.main'
  }

  const getScoreGrade = (score: number) => {
    if (score >= 4.5) return 'Excepțional'
    if (score >= 4.0) return 'Foarte Bine'
    if (score >= 3.5) return 'Bine'
    if (score >= 3.0) return 'Satisfăcător'
    if (score >= 2.0) return 'Sub Medie'
    return 'Insuficient'
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card elevation={1} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', overflow: 'auto' }}>
        {/* Header - Compact */}
        <Box display="flex" alignItems="center" mb={1}>
          <StarIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Performanță</Typography>
        </Box>

        {/* Score Principal - Compact */}
        <Box textAlign="center" mb={1.5}>
          <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: getScoreColor(score), lineHeight: 1 }}>{score.toFixed(1)}</Typography>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>/ 5.0</Typography>
          <Rating value={stars} readOnly size="small" sx={{ mt: 0.5 }} />
          <Box><Chip label={getScoreGrade(score)} color={score >= 4.0 ? 'success' : score >= 3.0 ? 'warning' : 'error'} size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} /></Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Rank & Percentage - Compact */}
        {(rank !== undefined || scorePercentage !== undefined) && (
          <Box mb={1}>
            {rank && totalMedics && (
              <Box display="flex" justifyContent="space-between" mb={0.25}>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Poziție:</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>#{rank} / {totalMedics}</Typography>
              </Box>
            )}
            {scorePercentage !== undefined && (
              <Box display="flex" justifyContent="space-between">
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Percentil:</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>Top {Math.round(scorePercentage)}%</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Breakdown - Compact */}
        {breakdown && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Detalii:</Typography>
            <Grid container spacing={0.25}>
              <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Scor bază</Typography></Grid>
              <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', fontWeight: 500 }}>{breakdown?.base_score?.toFixed(2) || '0.00'}</Typography></Grid>

              {breakdown && breakdown.completion_bonus > 0 && (<>
                <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'success.main' }}>+ Completare</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', color: 'success.main', fontWeight: 500 }}>+{breakdown.completion_bonus?.toFixed(2)}</Typography></Grid>
              </>)}
              {breakdown && breakdown.urgency_bonus > 0 && (<>
                <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'success.main' }}>+ Urgențe</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', color: 'success.main', fontWeight: 500 }}>+{breakdown.urgency_bonus?.toFixed(2)}</Typography></Grid>
              </>)}
              {breakdown && breakdown.pickup_penalty > 0 && (<>
                <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>- Răspuns</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', color: 'error.main', fontWeight: 500 }}>-{breakdown.pickup_penalty?.toFixed(2)}</Typography></Grid>
              </>)}
              {breakdown && breakdown.finalize_penalty > 0 && (<>
                <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>- Finalizare</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', color: 'error.main', fontWeight: 500 }}>-{breakdown.finalize_penalty?.toFixed(2)}</Typography></Grid>
              </>)}
              {breakdown && breakdown.unassign_penalty > 0 && (<>
                <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>- Unassign</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', color: 'error.main', fontWeight: 500 }}>-{breakdown.unassign_penalty?.toFixed(2)}</Typography></Grid>
              </>)}
              {breakdown && breakdown.overdue_penalty > 0 && (<>
                <Grid item xs={8}><Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>- Overdue</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.68rem', color: 'error.main', fontWeight: 500 }}>-{breakdown.overdue_penalty?.toFixed(2)}</Typography></Grid>
              </>)}
              <Grid item xs={12}><Divider sx={{ my: 0.25 }} /></Grid>
              <Grid item xs={8}><Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>FINAL</Typography></Grid>
              <Grid item xs={4} textAlign="right"><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: getScoreColor(score) }}>{score.toFixed(2)}</Typography></Grid>
            </Grid>
          </>
        )}
      </CardContent>
    </Card>
  )
}

